// src/components/PrintContainer.jsx
import React from "react";
import { Button } from "reactstrap";

import { renderBarcodeSvg } from "./engine/barcodeRender";

/**
 * A4 per faqe — 3×7 grid, iframe print, JsBarcode.
 * Padding (outer) aplikohet NË ÇDO PAGE, jo në sheet.
 */
const LAYOUT = {
  cols: 3,
  rows: 7,
  outerMM: 2, // skajet e faqes (2mm) për çdo faqe
  gutterMM: 1.8, // hapësira mes etiketave
  cardPadMM: 3, // padding brenda çdo etikete
  showGuides: true, // vija me pika për prerje
  fillGrid: true, // gjithmonë 21 etiketa për faqe
  // tipografia
  fontCodePx: 35,
  fontMetaPx: 20,
  fontSizePx: 20,
  // barkodi
  barcodeLineWidth: 0.7,
  barcodeHeight: 13,
  barcodeFontSize: 6,
};

/* ---------------- helpers ---------------- */
const escapeHTML = (str) =>
  String(str ?? "").replace(
    /[&<>"']/g,
    (s) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[s])
  );


const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/* --------------- barcodes (rendered locally, never from a CDN) --------------- */
class LegacyBarcodeError extends Error {}

const renderLegacyBarcode = (value, priceText) => {
  const result = renderBarcodeSvg(value, {
    width: LAYOUT.barcodeLineWidth,
    height: LAYOUT.barcodeHeight,
    displayValue: true,
    text: priceText, // çmimi poshtë barkodit
    fontOptions: "bold",
    font: "monospace",
    fontSize: LAYOUT.barcodeFontSize,
    textAlign: "center",
    textPosition: "bottom",
  });
  if (!result.ok) throw new LegacyBarcodeError(value);
  return result.svg;
};

/* --------------- printable HTML --------------- */
const buildPrintHTML = (items) => {
  const perPage = LAYOUT.cols * LAYOUT.rows;
  const pages = chunk(items, perPage);

  const pageHTML = pages
    .map((pageItems) => {
      const data = LAYOUT.fillGrid
        ? [
            ...pageItems,
            ...Array.from({
              length: Math.max(0, perPage - pageItems.length),
            }).map(() => ({})),
          ]
        : pageItems;

      const cards = data
        .map((p) => {
          const hasData = Boolean(
            p &&
              (p.code ||
                p.barcode ||
                p.color ||
                p.number ||
                p.price ||
                p.image_url)
          );
          const code = escapeHTML(p?.code ?? "");
          const color = escapeHTML(p?.color ?? "");
          const number = escapeHTML(p?.number ?? "");
          const priceText =
            p?.price !== undefined && p?.price !== "" ? `${p.price}€` : "";
          const barcodeValue = String(p?.barcode ?? p?.code ?? "");
          const barcodeSvg = barcodeValue ? renderLegacyBarcode(barcodeValue, priceText) : "";
          const imgSrc = p?.image_url
            ? `https://blackandwhite-images.s3.eu-north-1.amazonaws.com/${encodeURIComponent(
                p.image_url
              )}`
            : "";

          return `
          <div class="card${LAYOUT.showGuides ? " guide" : ""}${
            hasData ? "" : " blank"
          }">
            <div class="row row-top">
              <div class="${imgSrc ? "code" : "code text-center"}">${code}</div>
              <div class="thumb">${
                imgSrc ? `<img alt="" src="${imgSrc}" />` : ""
              }</div>
            </div>

            <div class="row row-mid">
              <div class="meta">
                ${
                  color
                    ? `<span class="chip chip-color" title="Color">${color}</span>`
                    : ""
                }
              </div>
              <div class="meta">
                ${
                  number
                    ? `<span class="chip chip-size" title="Size">${number}</span>`
                    : ""
                }
              </div>
            </div>

            <div class="row row-bottom">
              <div class="barcode">${barcodeSvg}</div>
            </div>
          </div>
        `;
        })
        .join("");

      return `<section class="page">${cards}</section>`;
    })
    .join("");

  const {
    cols,
    rows,
    outerMM,
    gutterMM,
    cardPadMM,
    fontCodePx,
    fontMetaPx,
    fontSizePx,
  } = LAYOUT;

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Labels</title>
<style>
  :root{
    --cols:${cols};
    --rows:${rows};
    --outer:${outerMM}mm;
    --gutter:${gutterMM}mm;
    --cardpad:${cardPadMM}mm;
    --code-px:${fontCodePx};
    --meta-px:${fontMetaPx};
    --text-px:${fontSizePx};
  }

  *{ box-sizing:border-box; }
  html, body { margin:0; padding:0; background:#fff; color:#000; }

  /* kontrollojmë vetë skajet; printer margins off */
  @page{ size: A4 portrait; margin: 0; }

  body{
    /* nuk e detyrojmë height këtu; secila .page mban A4 me padding-in e vet */
    // font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Helvetica Neue", Helvetica;
  }

  /* Kontejner opsional për të mbajtur faqet — PA padding këtu */
  .sheet{
    /* pa padding; kështu çdo .page do ketë padding-un e vet */
  }

  /* ÇDO FAQE është A4 me padding-in e vet */
  .page{
    width: 210mm;
    height: 297mm;
    padding: var(--outer);          /* padding për faqe */
    box-sizing: border-box;         /* padding brenda 210×297 */
    display: grid;
    grid-template-columns: repeat(var(--cols), 1fr);
    column-gap: var(--gutter);
    row-gap: var(--gutter);
    grid-auto-rows: calc(
      (100% - (var(--rows) - 1)*var(--gutter)) / var(--rows)
    );
    break-after: page;              /* page break pas secilës faqe */
    background: #fff;
  }

  .card{
    padding: var(--cardpad);
    display: grid;
    grid-template-rows: auto auto 1fr; /* top / mid / bottom */
    align-items: center;
    justify-items: stretch;
    overflow: hidden;
    border-radius: 0.8mm;           /* qoshe të buta */
    background: #fff;
  }

  /* shenja prerjeje shumë të holla, pa “heavy borders” */
  .guide{ outline: 0.2mm dashed rgba(0,0,0,.25); outline-offset: -0.2mm; }

  .blank{ opacity: 0.12; }          /* qelizat bosh për orientim */

  .row{ width:100%; }
  .row-top{ display:flex; gap: 1.6mm; align-items:flex-start; }
  .row-mid{ display:flex; align-items:center; justify-content:space-between; margin-top: 0.6mm; }
  .row-bottom{ margin-top: 0.4mm; }

  // .row-bottom{ 
  //   margin-top: 0.4mm; 
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;
  //   // margin-top: 0.6mm;
  // }

  .code{
    flex:1 1 auto;
    min-width:0;
    word-break: break-word;
    font-weight: 800;
    letter-spacing: .1px;
    line-height: 1.05;
    font-size: calc(var(--code-px) * 1px);
  }
  .text-center{ text-align: center; }
  .thumb{ flex:0 0 auto; }
  .thumb img{
    display:block;
    width: 68px;
    height: 36px;
    /* object-fit: contain; */
    border: 0.2mm dashed rgba(0,0,0,.55);
    border-radius: 0.6mm;
    padding: 0.3mm;
  }

  .meta{ display:flex; gap: 0.8mm; align-items:center; }
  .chip{
    display:inline-flex; align-items:center; justify-content:center;
    /* border: 0.22mm solid #000; */ /* opsionale */
    border-radius: 1.2mm;
    /* padding: 0 1.8mm; */
    font-weight: 800;
    letter-spacing: .1px;
    white-space: nowrap;
  }
  .chip-color{ font-size: calc(var(--meta-px) * 1px); }
  .chip-size{
    font-size: calc(var(--meta-px) * 1px);
    border: 1px solid black;
    padding: 3px;
    border-radius: 9px;
  }

  .price{
    font-weight: 800;
    font-size: calc(var(--meta-px) * 1px);
    line-height: 1;
    padding: 0 1.6mm;
    border: 0.22mm solid #000;
    border-radius: 1.2mm;
    min-width: 14mm;
    text-align: center;
  }

  .barcode{ width:100%; overflow:hidden; }
  .barcode svg{ width:100% !important; height:auto !important; display:block; }

  // .barcode{ 
  //   // width:100%; 
  //   overflow:visible; 
  // }
  // .barcode svg{ 
  //     // width:100% !important; 
  //     // height:auto !important; 
  //     display:block; 
  // }

  @media print { .page{ break-inside: avoid; } }
</style>
</head>
<body>
  <div class="sheet">
    ${pageHTML}
  </div>

  <script>
    (function(){
      function waitForImages(){
        var imgs = Array.from(document.images || []);
        if (imgs.length === 0) return Promise.resolve();
        return Promise.all(imgs.map(function(img){
          return img.complete ? Promise.resolve() : new Promise(function(res){ img.onload = img.onerror = res; });
        }));
      }

      function ready(){
        var done = function(){ setTimeout(function(){ window.print(); }, 40); };
        Promise.resolve().then(waitForImages).then(done);
      }
      if (document.readyState === 'complete' || document.readyState === 'interactive') ready();
      else document.addEventListener('DOMContentLoaded', ready);
    })();
  </script>
</body>
</html>`;
};

/* --------------- component: iframe-only print --------------- */
const PrintContainer = ({ printProducts = [] }) => {
  const selectedProducts = React.useMemo(
    () =>
      (Array.isArray(printProducts) ? printProducts : []).filter(
        (p) => p?.selected
      ),
    [printProducts]
  );

  const handlePrint = () => {
    try {
      if (selectedProducts.length === 0) {
        alert("No products selected for printing");
        return;
      }

      const html = buildPrintHTML(selectedProducts);

      // Removed: open in new tab (blocked by popup blockers)
      const win = window.open("", "_blank");
      if (win && win.document) {
        win.document.open();
        win.document.write(html);
        win.document.close();
        // optional: close window after printing
        win.addEventListener("afterprint", () => {
          try {
            // win.close();
          } catch {}
        });
        return;
      }
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      const { contentDocument, contentWindow } = iframe;
      if (!contentDocument || !contentWindow)
        throw new Error("Print iframe not available");

      contentDocument.open();
      contentDocument.write(html);
      contentDocument.close();

      contentWindow.addEventListener("afterprint", () => {
        setTimeout(() => document.body.removeChild(iframe), 400);
      });
    } catch (err) {
      if (err instanceof LegacyBarcodeError) {
        alert(`This barcode cannot be printed: ${err.message}`);
        return;
      }
      console.error("Printing failed:", err);
      alert("Printing failed. Please try again.");
    }
  };

  return (
    <div>
      <Button
        color="dark"
        size="sm"
        onClick={handlePrint}
        disabled={selectedProducts.length === 0}
      >
        Print ({selectedProducts.length})
      </Button>
    </div>
  );
};

export default PrintContainer;
